import { useEffect, useState } from 'react'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Container,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  HStack,
  Button,
  Select,
  Card,
  CardBody,
  SimpleGrid,
  useBreakpointValue,
  Divider,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { getAdminAssetList, createAsset, updateAsset, deleteAdminAsset } from '../../services/apiService'
import dayjs from 'dayjs'
import React from 'react'

const adminNavItems = [
  { label: '회원 관리', path: '/admin/user' },
  { label: '커뮤니티 관리', path: '/admin/community' },
  { label: 'Q&A 관리', path: '/admin/qna' },
  { label: '공지사항 관리', path: '/admin/notice' },
  { label: '자산 관리', path: '/admin/asset' },
  { label: '경매사례 관리', path: '/admin/auction-case' },
]

// 소재지 옵션
const LOCATION_OPTIONS = [
  '경기도',
  '강원도',
  '서울특별시',
  '인천광역시',
  '충청남도',
  '대전광역시',
  '세종특별자치시',
  '충청북도',
  '경상북도',
  '전라북도',
  '광주광역시',
  '전라남도',
  '대구광역시',
  '경상남도',
  '제주특별자치도',
  '울산광역시',
  '부산광역시',
]

interface Asset {
  id: number
  location: string
  area: number
  appraisalValue: string
  note: string | null
  createdAt: string
  updatedAt: string | null
}

const Asset = () => {
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })

  // 모달 상태
  const [isEdit, setIsEdit] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formLocation, setFormLocation] = useState('')
  const [formArea, setFormArea] = useState('')
  const [formAppraisalValue, setFormAppraisalValue] = useState('')
  const [formNote, setFormNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // 삭제 확인 모달
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const cancelRef = React.useRef<HTMLButtonElement>(null)

  const fetchAssets = async () => {
    setLoading(true)
    try {
      const { data: res } = await getAdminAssetList(page, limit)
      if (res.statusCode === 200) {
        setAssets(res.data.assets)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error) {
      console.error('Error fetching assets:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAssets()
  }, [page, limit])

  const handleCreate = () => {
    setIsEdit(false)
    setEditingId(null)
    setFormLocation('')
    setFormArea('')
    setFormAppraisalValue('')
    setFormNote('')
    onOpen()
  }

  const handleEdit = (asset: Asset) => {
    setIsEdit(true)
    setEditingId(asset.id)
    setFormLocation(asset.location)
    setFormArea(String(asset.area))
    setFormAppraisalValue(asset.appraisalValue)
    setFormNote(asset.note || '')
    onOpen()
  }

  const handleSubmit = async () => {
    if (!formLocation) {
      toast({
        title: '입력 오류',
        description: '소재지를 선택해주세요.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    if (!formArea || isNaN(Number(formArea))) {
      toast({
        title: '입력 오류',
        description: '면적을 올바르게 입력해주세요.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    if (!formAppraisalValue || isNaN(Number(formAppraisalValue))) {
      toast({
        title: '입력 오류',
        description: '감정가를 올바르게 입력해주세요.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setSubmitting(true)
    try {
      if (isEdit && editingId) {
        const { data: res } = await updateAsset(
          editingId,
          formLocation,
          Number(formArea),
          formAppraisalValue,
          formNote || undefined
        )
        if (res.statusCode === 200) {
          toast({
            title: '수정 완료',
            description: '자산이 수정되었습니다.',
            status: 'success',
            duration: 3000,
            isClosable: true
          })
          onClose()
          fetchAssets()
        }
      } else {
        const { data: res } = await createAsset(
          formLocation,
          Number(formArea),
          formAppraisalValue,
          formNote || undefined
        )
        if (res.statusCode === 200) {
          toast({
            title: '등록 완료',
            description: '자산이 등록되었습니다.',
            status: 'success',
            duration: 3000,
            isClosable: true
          })
          onClose()
          fetchAssets()
        }
      }
    } catch (error: any) {
      toast({
        title: isEdit ? '수정 실패' : '등록 실패',
        description: error.response?.data?.message || '오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteClick = (id: number) => {
    setDeleteId(id)
    setDeleteAlertOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteId) return

    try {
      const { data: res } = await deleteAdminAsset(deleteId)
      if (res.statusCode === 200) {
        toast({
          title: '삭제 완료',
          description: '자산이 삭제되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        fetchAssets()
      }
    } catch (error: any) {
      toast({
        title: '삭제 실패',
        description: error.response?.data?.message || '삭제 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setDeleteAlertOpen(false)
      setDeleteId(null)
    }
  }

  // 숫자 포맷팅 (천단위 콤마)
  const formatNumber = (num: number | string) => {
    return Number(num).toLocaleString()
  }

  return (
    <DefaultLayout>
      <AdminNav items={adminNavItems} />
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg">자산 관리</Heading>
            <Button colorScheme="blue" onClick={handleCreate}>
              자산 등록
            </Button>
          </HStack>

          <Box
            p={6}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
          >
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    총 {total}건의 자산
                  </Text>
                  <Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value))
                      setPage(1)
                    }}
                    width="120px"
                    size="sm"
                  >
                    <option value={10}>10개씩</option>
                    <option value={20}>20개씩</option>
                    <option value={50}>50개씩</option>
                  </Select>
                </HStack>

                {isMobile ? (
                  // 모바일: 카드 형태
                  <VStack spacing={4} align="stretch">
                    {assets.length === 0 ? (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">등록된 자산이 없습니다.</Text>
                      </Box>
                    ) : (
                      assets.map((asset) => (
                        <Card key={asset.id} size="sm">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <HStack justify="space-between">
                                <Text fontWeight="bold" fontSize="lg">
                                  {asset.location}
                                </Text>
                                <Text fontSize="sm" color="gray.500">#{asset.id}</Text>
                              </HStack>
                              <Divider />
                              <SimpleGrid columns={2} spacing={2} fontSize="sm">
                                <Text color="gray.600">면적:</Text>
                                <Text>{formatNumber(asset.area)} m²</Text>
                                <Text color="gray.600">감정가:</Text>
                                <Text>{formatNumber(asset.appraisalValue)} 원</Text>
                                <Text color="gray.600">비고:</Text>
                                <Text>{asset.note || '-'}</Text>
                                <Text color="gray.600">등록일:</Text>
                                <Text>
                                  {dayjs(asset.createdAt).format('YYYY-MM-DD HH:mm')}
                                </Text>
                              </SimpleGrid>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleEdit(asset)}
                                >
                                  수정
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => handleDeleteClick(asset.id)}
                                >
                                  삭제
                                </Button>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </VStack>
                ) : (
                  // 데스크탑: 테이블 형태
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>ID</Th>
                          <Th>소재지</Th>
                          <Th isNumeric>면적 (m²)</Th>
                          <Th isNumeric>감정가 (원)</Th>
                          <Th>비고</Th>
                          <Th>등록일</Th>
                          <Th>관리</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {assets.length === 0 ? (
                          <Tr>
                            <Td colSpan={7} textAlign="center" py={8}>
                              <Text color="gray.500">등록된 자산이 없습니다.</Text>
                            </Td>
                          </Tr>
                        ) : (
                          assets.map((asset) => (
                            <Tr key={asset.id}>
                              <Td>{asset.id}</Td>
                              <Td>{asset.location}</Td>
                              <Td isNumeric>{formatNumber(asset.area)}</Td>
                              <Td isNumeric>{formatNumber(asset.appraisalValue)}</Td>
                              <Td>{asset.note || '-'}</Td>
                              <Td>
                                {dayjs(asset.createdAt).format('YYYY-MM-DD HH:mm')}
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <Button
                                    size="sm"
                                    colorScheme="blue"
                                    onClick={() => handleEdit(asset)}
                                  >
                                    수정
                                  </Button>
                                  <Button
                                    size="sm"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(asset.id)}
                                  >
                                    삭제
                                  </Button>
                                </HStack>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {totalPages > 0 && (
                  <HStack justify="center" spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      isDisabled={page === 1}
                    >
                      이전
                    </Button>
                    <Text fontSize="sm">
                      {page} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      isDisabled={page >= totalPages}
                    >
                      다음
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>

      {/* 등록/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEdit ? '자산 수정' : '자산 등록'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>소재지</FormLabel>
                <Select
                  placeholder="소재지 선택"
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                >
                  {LOCATION_OPTIONS.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>면적 (m²)</FormLabel>
                <Input
                  type="number"
                  placeholder="면적을 입력하세요"
                  value={formArea}
                  onChange={(e) => setFormArea(e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>감정가 (원)</FormLabel>
                <Input
                  type="number"
                  placeholder="감정가를 입력하세요"
                  value={formAppraisalValue}
                  onChange={(e) => setFormAppraisalValue(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>비고</FormLabel>
                <Textarea
                  placeholder="비고를 입력하세요"
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              {isEdit ? '수정' : '등록'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 모달 */}
      <AlertDialog
        isOpen={deleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteAlertOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              자산 삭제
            </AlertDialogHeader>

            <AlertDialogBody>
              정말로 이 자산을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteAlertOpen(false)}>
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default Asset
